import type { MedplumClient } from '@medplum/core';
import type { Attachment } from '@medplum/fhirtypes';
import {
  downloadBase64Attachment,
  sanitizeDownloadFilename,
  triggerBlobDownload,
} from './fileDownload';
import { UserFacingError } from './userFacingError';

/**
 * Normalize Binary refs for `medplum.download()` to `Binary/{id}`.
 *
 * Hosted Medplum rewrites Attachment.url to short-lived storage / `/storage/` URLs on read.
 * Passing those URLs to `medplum.download()` attaches Authorization and fails CORS
 * ("Failed to fetch"). Always prefer the FHIR `Binary/{id}` form.
 */
export function normalizeBinaryUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith('Binary/')) return trimmed;

  const fhirMatch = trimmed.match(/(?:^|\/)Binary\/([A-Za-z0-9-]+)/);
  if (fhirMatch?.[1]) return `Binary/${fhirMatch[1]}`;

  const storageBinaryMatch = trimmed.match(/\/binary\/([A-Za-z0-9-]+)/i);
  if (storageBinaryMatch?.[1]) return `Binary/${storageBinaryMatch[1]}`;

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      const isMedplumStorage =
        /(^|\.)storage\.medplum\.com$/i.test(parsed.hostname) ||
        /\/storage(\/|$)/i.test(parsed.pathname);
      if (isMedplumStorage) {
        const parts = parsed.pathname.split('/').filter(Boolean);
        const storageIndex = parts.findIndex((part) => part.toLowerCase() === 'storage');
        const binaryId = storageIndex >= 0 ? parts[storageIndex + 1] : parts[0];
        if (binaryId && /^[A-Za-z0-9-]+$/.test(binaryId)) {
          return `Binary/${binaryId}`;
        }
      }
    } catch {
      // fall through
    }
  }

  return trimmed;
}

function isMedplumBinaryUrl(url: string): boolean {
  return url.startsWith('Binary/') || /Binary\/[A-Za-z0-9-]+/.test(url);
}

/**
 * Download a FHIR Attachment via blob URL (no new tab).
 */
export async function downloadFhirAttachment(
  attachment: Attachment,
  filename: string | undefined,
  medplum?: MedplumClient,
): Promise<void> {
  const name = sanitizeDownloadFilename(filename ?? attachment.title ?? 'download');

  if (attachment.data) {
    downloadBase64Attachment(attachment.data, name, attachment.contentType ?? 'application/octet-stream');
    return;
  }

  if (!attachment.url) {
    throw new UserFacingError('No attachment data or URL is available for download.');
  }

  if (medplum && isMedplumBinaryUrl(attachment.url)) {
    const blob = await medplum.download(normalizeBinaryUrl(attachment.url));
    triggerBlobDownload(blob, name);
    return;
  }

  if (/^https?:\/\//i.test(attachment.url)) {
    const response = await fetch(attachment.url);
    if (!response.ok) {
      throw new UserFacingError('Could not fetch attachment for download.');
    }
    const blob = await response.blob();
    triggerBlobDownload(blob, name);
    return;
  }

  if (medplum) {
    const blob = await medplum.download(normalizeBinaryUrl(attachment.url));
    triggerBlobDownload(blob, name);
    return;
  }

  throw new Error('Medplum client is required to download this attachment.');
}
